import React, { useEffect, useRef, useState } from 'react';
import { DayPicker, DateRange } from 'react-day-picker';
import 'react-day-picker/style.css';
import './CreatedDateRangePicker.css';
import { Calendar, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';

interface CreatedDateRangePickerProps {
  value?: DateRange;
  onChange: (range: DateRange | undefined) => void;
  showLabel?: boolean;
  className?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
}

export function CreatedDateRangePicker({
  value,
  onChange,
  showLabel = true,
  className = '',
  open,
  onOpenChange,
  hideTrigger = false,
}: CreatedDateRangePickerProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open ?? internalOpen;
  const pickerRef = useRef<HTMLDivElement>(null);
  const currentMonth = new Date();
  const previousMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);

  const setIsOpen = (nextOpen: boolean) => {
    if (open === undefined) {
      setInternalOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const formatRangeLabel = () => {
    if (!value?.from && !value?.to) return 'All Period';

    const formatDate = (date: Date) =>
      date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

    if (value?.from && !value?.to) return formatDate(value.from);
    if (value?.from && value?.to) return `${formatDate(value.from)} - ${formatDate(value.to)}`;

    return 'All Period';
  };

  const normalizeCalendarDate = (date: Date): Date =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());

  return (
    <div className={`relative ${className}`} ref={pickerRef}>
      {!hideTrigger && (
        <>
          {showLabel && <label className="text-sm font-medium text-gray-700 block mb-2">Created</label>}
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="flex h-10 w-full items-center justify-between rounded-lg border border-input bg-background px-3 py-2 text-sm"
          >
            <div className="flex items-center gap-2 whitespace-nowrap">
              <Calendar className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">
                <span className="text-gray-500">Created: </span>
                <span className="font-medium text-gray-900">{formatRangeLabel()}</span>
              </span>
            </div>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </button>
        </>
      )}

      {isOpen && (
        <div className="cb-datepicker absolute right-0 mt-2 z-50 w-[720px] rounded-2xl border border-gray-200 bg-white p-4 shadow-lg">
          <DayPicker
            mode="range"
            numberOfMonths={2}
            reverseMonths={false}
            pagedNavigation
            showOutsideDays
            selected={value}
            defaultMonth={value?.from ?? previousMonth}
            disabled={{ after: new Date() }}
            className="cb-rdp"
            classNames={{
              months: 'cb-rdp-months',
              month: 'cb-rdp-month',
            }}
            modifiersClassNames={{
              disabled: 'text-gray-300 cursor-not-allowed',
            }}
            onSelect={(range) => {
              // Keep popover open while user picks start/end range.
              if (!range) {
                onChange(undefined);
                return;
              }

              onChange({
                from: range.from ? normalizeCalendarDate(range.from) : undefined,
                to: range.to ? normalizeCalendarDate(range.to) : undefined,
              });
            }}
          />
          <div className="mt-2 flex justify-end gap-2">
            {value?.from && !value?.to && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onChange({ from: value.from, to: value.from });
                  setIsOpen(false);
                }}
              >
                Select Day
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onChange(undefined)}
            >
              Clear
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsOpen(false)}
            >
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
